import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://buyerbeacon.pro";
const DEFAULT_IMAGE = `${SITE}/og/default.png`;

// Map known route prefixes to OG variants. Anything not matched falls back to default.
const OG_BY_PATH = {
  "/": "/og/default.png",
  "/about": "/og/about.png",
  "/pricing": "/og/pricing.png",
  "/demo": "/og/demo.png",
  "/login": "/og/demo.png",
  "/affiliate": "/og/affiliate.png",
  "/resources": "/og/resources.png",
  "/privacy": "/og/privacy.png",
  "/terms": "/og/terms.png",
  "/security": "/og/security.png",
  "/compliance": "/og/compliance.png",
};

function ogImageForPath(path) {
  if (!path) return DEFAULT_IMAGE;
  // exact match first
  if (OG_BY_PATH[path]) return `${SITE}${OG_BY_PATH[path]}`;
  // resources/article — use resources variant
  if (path.startsWith("/resources")) return `${SITE}/og/resources.png`;
  // solutions/* and industries/* — reuse default brand variant
  return DEFAULT_IMAGE;
}

/**
 * SEO meta-tag wrapper. Set per-page title, description, canonical, OG, Twitter,
 * and optional JSON-LD structured data.
 *
 * Props:
 *  title         — page title (we append " · BuyerBeacon Pro" if not present)
 *  description   — meta description (under 160 chars recommended)
 *  path          — canonical path (e.g. "/about"); defaults to current pathname
 *  image         — OG image absolute URL
 *  type          — og:type ("website" or "article")
 *  noIndex       — set true for thank-you pages, opt-out success, etc.
 *  jsonLd        — optional structured-data object (will be JSON.stringify'd)
 */
export default function SEO({
  title,
  description,
  path,
  image,
  type = "website",
  noIndex = false,
  jsonLd,
}) {
  const fullTitle = title?.includes("BuyerBeacon")
    ? title
    : `${title} · BuyerBeacon Pro`;
  const resolvedPath = path || (typeof window !== "undefined" ? window.location.pathname : "");
  const canonical = `${SITE}${resolvedPath}`;
  const resolvedImage = image || ogImageForPath(resolvedPath);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="BuyerBeacon Pro" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedImage} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// Reusable structured-data objects
export const ORG_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "BuyerBeacon Pro",
  url: SITE,
  logo: `${SITE}/brand/logo.svg`,
  description:
    "BuyerBeacon Pro illuminates ready-to-buy customers using intent data, identity resolution, and multi-channel activation — for small businesses and agencies.",
  email: "admin@buyerbeacon.pro",
  address: {
    "@type": "PostalAddress",
    streetAddress: "32 N Gould St., Ste R",
    addressLocality: "Sheridan",
    addressRegion: "WY",
    postalCode: "82801",
    addressCountry: "US",
  },
  sameAs: [],
};

export function softwareJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "BuyerBeacon Pro",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: "499",
      priceCurrency: "USD",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "127",
    },
  };
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}
